import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetAdCategoryQuery, useGetCategoryTypesQuery, useCreateCategoryTypeMutation, useUpdateCategoryTypeMutation, useDeleteCategoryTypeMutation, useToggleCategoryTypeStatusMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Modal from '../../../components/Modal';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';
import { useLanguage } from '../../../context/LanguageContext';

interface CategoryTypeRow {
  id?: number;
  title: string;
  title_ar: string;
  description: string;
  description_ar: string;
  status: boolean;
  sort_order: number;
  listing_type: 'supply' | 'demand';
}

const emptyType = (): CategoryTypeRow => ({
  title: '',
  title_ar: '',
  description: '',
  description_ar: '',
  status: true,
  sort_order: 0,
  listing_type: 'supply',
});

const AdminAdCategoryTypes: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const categoryId = parseInt(id!);

  const { data: categoryData, isLoading: isLoadingCategory } = useGetAdCategoryQuery(categoryId);
  const { data: typesData, isLoading: isLoadingTypes, refetch } = useGetCategoryTypesQuery(categoryId);
  const [createCategoryType] = useCreateCategoryTypeMutation();
  const [updateCategoryType] = useUpdateCategoryTypeMutation();
  const [deleteCategoryType] = useDeleteCategoryTypeMutation();
  const [toggleCategoryTypeStatus] = useToggleCategoryTypeStatusMutation();

  const [types, setTypes] = useState<CategoryTypeRow[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<CategoryTypeRow | null>(null);
  const [searchParams] = useSearchParams();
  const canEdit = searchParams.get('can_edit') === '1';

  const category = categoryData?.data;

  useEffect(() => {
    if (typesData?.data) {
      setTypes(
        typesData.data.map((t: any) => ({
          id: t.id,
          title: t.title || '',
          title_ar: t.title_ar || '',
          description: t.description || '',
          description_ar: t.description_ar || '',
          status: t.status ?? true,
          sort_order: t.sort_order ?? 0,
          listing_type: t.listing_type || 'supply',
        }))
      );
    } else {
      setTypes([]);
    }
  }, [typesData]);

  const openAddModal = () => {
    setEditingType({ ...emptyType(), sort_order: types.length });
    setIsModalOpen(true);
  };

  const openEditModal = (type: CategoryTypeRow) => {
    setEditingType(JSON.parse(JSON.stringify(type)));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingType(null);
    setIsModalOpen(false);
  };

  const handleChange = (field: keyof CategoryTypeRow, value: any) => {
    if (!editingType) return;
    setEditingType(prev => ({ ...prev!, [field]: value }));
  };

  const handleSave = async () => {
    if (!editingType) return;

    if (!editingType.title || !editingType.title_ar) {
      showError(t('Title (English and Arabic) is required'));
      return;
    }

    try {
      const payload = {
        category_id: categoryId,
        title: editingType.title,
        title_ar: editingType.title_ar,
        description: editingType.description,
        description_ar: editingType.description_ar,
        status: editingType.status,
        sort_order: editingType.sort_order,
        listing_type: editingType.listing_type,
      };

      if (editingType.id) {
        await updateCategoryType({ id: editingType.id, data: payload }).unwrap();
        showSuccess(t('Category type updated successfully'));
      } else {
        await createCategoryType(payload).unwrap();
        showSuccess(t('Category type created successfully'));
      }

      closeModal();
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to save category type. Please try again.'));
    }
  };

  const handleDelete = async (type: CategoryTypeRow) => {
    if (!type.id) return;

    const confirmed = await confirmAction(t('Delete Category Type'), t('Are you sure you want to delete this category type?'));
    if (!confirmed) return;

    try {
      await deleteCategoryType(type.id).unwrap();
      showSuccess(t('Category type deleted successfully'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to delete category type. Please try again.'));
    }
  };

  const handleToggleStatus = async (type: CategoryTypeRow) => {
    if (!type.id) return;
    try {
      await toggleCategoryTypeStatus({ id: type.id, status: !type.status }).unwrap();
      showSuccess(t('Status updated successfully'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update status. Please try again.'));
    }
  };

  if (isLoadingCategory || isLoadingTypes) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Category Types')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-categories'); }} className="text-muted text-hover-primary">
                    {t('Ad Categories')}
                  </a>
                </li>
                {category?.parent_id && (
                  <>
                    <li className="breadcrumb-item">
                      <span className="bullet bg-gray-400 w-5px h-2px"></span>
                    </li>
                    <li className="breadcrumb-item text-muted">
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/admin/ad-categories?parent_id=${category.parent_id}`); }} className="text-muted text-hover-primary">
                        {category.parent?.title || t('Subcategories')}
                      </a>
                    </li>
                  </>
                )}
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {category?.title || t('Category')} - {t('Types')}
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => {
                  if (category?.parent_id) {
                    navigate(`/admin/ad-categories?parent_id=${category.parent_id}`);
                  } else {
                    navigate('/admin/ad-categories');
                  }
                }}
              >
                {t('Back')}
              </button>
              {canEdit &&
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={openAddModal}
                >
                  {t('Add Category Type')}
                </button>
              }
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold fs-3 mb-1">{t('Category Types for:')} {category?.title || t('Category')}</span>
                    <span className="text-muted mt-1 fw-semibold fs-7">{t('Manage types for this ad category')}</span>
                  </h3>
                </div>
              </div>
              <div className="card-body pt-0">
                {types.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-muted">{t('No category types found. Click "Add Category Type" to create one.')}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                      <thead>
                        <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                          <th className="min-w-50px">#</th>
                          <th className="min-w-150px">{t('Title (EN)')}</th>
                          <th className="min-w-150px">{t('Title (AR)')}</th>
                          <th className="min-w-200px">{t('Description (EN)')}</th>
                          <th className="min-w-200px">{t('Description (AR)')}</th>
                          <th className="min-w-100px">{t('Listing Type')}</th>
                          <th className="min-w-80px">{t('Status')}</th>
                          <th className="min-w-80px">{t('Sort Order')}</th>
                          <th className="text-end min-w-100px">{t('Actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600 fw-semibold">
                        {types.map((type, index) => (
                          <tr key={type.id || index}>
                            <td>{index + 1}</td>
                            <td>{type.title}</td>
                            <td>{type.title_ar}</td>
                            <td>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: 200 }}>
                                {type.description || '-'}
                              </span>
                            </td>
                            <td>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: 200 }}>
                                {type.description_ar || '-'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${type.listing_type === 'demand' ? 'badge-light-info' : 'badge-light-success'}`}>
                                {type.listing_type === 'demand' ? t('Demand') : t('Supply')}
                              </span>
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={type.status === true}
                                  onChange={() => handleToggleStatus(type)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </td>
                            <td>{type.sort_order}</td>
                            {canEdit &&
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                  <button
                                    className="btn btn-icon btn-sm btn-light-primary"
                                    onClick={() => openEditModal(type)}
                                    title={t('Edit')}
                                  >
                                    <i className="fas fa-pencil-alt fs-6"></i>
                                  </button>
                                  <button
                                    className="btn btn-icon btn-sm btn-light-danger"
                                    onClick={() => handleDelete(type)}
                                    title={t('Delete')}
                                  >
                                    <i className="fas fa-trash fs-6"></i>
                                  </button>
                                </div>
                              </td>
                            }
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingType?.id ? t('Edit Category Type') : t('Add Category Type')}
        size="lg"
        footer={
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-light" onClick={closeModal}>
              {t('Cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              {t('Save Changes')}
            </button>
          </div>
        }
      >
        {editingType && (
          <div className="d-flex flex-column gap-4">
            <div className="row">
              <div className="col-md-6">
                <label className="form-label required">{t('Title (English)')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingType.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder={t('Enter title in English')}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label required">{t('Title (Arabic)')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingType.title_ar}
                  onChange={(e) => handleChange('title_ar', e.target.value)}
                  placeholder={t('Enter title in Arabic')}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <label className="form-label">{t('Description (English)')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={editingType.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('Enter description in English')}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t('Description (Arabic)')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={editingType.description_ar}
                  onChange={(e) => handleChange('description_ar', e.target.value)}
                  placeholder={t('Enter description in Arabic')}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <label className="form-label required">{t('Listing Type')}</label>
                <div className="d-flex gap-6 mt-1">
                  {(['supply', 'demand'] as const).map((opt) => (
                    <label
                      key={opt}
                      className="d-flex align-items-center gap-2 cursor-pointer"
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        type="radio"
                        className="form-check-input mt-0"
                        name="listing_type"
                        value={opt}
                        checked={editingType.listing_type === opt}
                        onChange={() => handleChange('listing_type', opt)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span className="fw-semibold fs-6 text-capitalize">{opt === 'supply' ? t('Supply') : t('Demand')}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <label className="form-label">{t('Sort Order')}</label>
                <input
                  type="number"
                  className="form-control"
                  value={editingType.sort_order}
                  onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="col-md-6 d-flex align-items-center pt-8">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="typeStatus"
                    checked={editingType.status}
                    onChange={(e) => handleChange('status', e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label className="form-check-label fw-semibold ms-2" htmlFor="typeStatus">
                    {t('Active')}
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminAdCategoryTypes;
